import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { blogPosts } from "../../data/blogPosts";
import AuditImg from "../../assets/Audit.jpg";
import PuzzleImg from "../../assets/Puzzle.jpg";
import TrainingImg from "../../assets/Training.jpg";

const BlogList = () => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(() => {
    const set = new Set(blogPosts.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, []);

  const coverBySlug = useMemo(
    () => ({
      "audit-ready-without-chaos": AuditImg,
      "disciplined-problem-solving": PuzzleImg,
      "training-for-adoption": TrainingImg
    }),
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return blogPosts
      .filter((p) => (activeCategory === "All" ? true : p.category === activeCategory))
      .filter((p) => {
        if (!q) return true;
        return (
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
        );
      });
  }, [query, activeCategory]);

  return (
    <section className="relative w-full bg-white overflow-x-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-44 -right-44 h-[30rem] w-[30rem] rounded-full bg-[color:var(--brand-accent)]/10 blur-3xl" />
        <div className="absolute -bottom-52 -left-52 h-[36rem] w-[36rem] rounded-full bg-[color:var(--brand)]/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-18 sm:py-20">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[color:var(--navy)]">
              Latest posts
            </h2>
            <p className="mt-3 text-[color:var(--muted)]">
              Short reads designed for adoption and real operational constraints.
            </p>
          </div>

          <div className="flex w-full flex-col gap-4 lg:w-auto lg:items-end">
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:justify-end">
              <div className="flex w-full sm:w-[320px] items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M10.5 18C14.1 18 17 15.1 17 11.5C17 7.9 14.1 5 10.5 5C6.9 5 4 7.9 4 11.5C4 15.1 6.9 18 10.5 18Z"
                    stroke="var(--brand)"
                    strokeWidth="2"
                  />
                  <path
                    d="M20 20L18 18"
                    stroke="var(--brand-accent)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search posts"
                  className="w-full bg-transparent text-sm outline-none text-[color:var(--navy)] placeholder:text-[color:var(--muted)]"
                />
              </div>

              <div className="flex w-full sm:w-auto justify-start sm:justify-end">
                <div className="inline-flex w-full sm:w-auto flex-wrap items-center gap-2 rounded-2xl border border-[color:var(--border)] bg-white p-2">
                  {categories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setActiveCategory(c)}
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-xl px-4 py-2 text-xs font-semibold transition ${
                        activeCategory === c
                          ? "bg-[color:var(--brand)] text-white shadow-sm"
                          : "bg-[color:var(--surface)] text-[color:var(--navy)] hover:bg-white border border-[color:var(--border)]"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {filtered.map((p) => {
            const cover = coverBySlug[p.slug] || p.cover;

            return (
              <NavLink
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-[color:var(--border)] bg-white transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative h-44 w-full overflow-hidden">
                  {cover ? (
                    <img
                      src={cover}
                      alt={p.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full bg-[color:var(--navy)]">
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-black/20 to-black/40" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                    {p.category}
                    <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-accent)]" />
                    {p.readTime}
                  </div>
                </div>

                <div className="p-7">
                  <div className="text-xs font-semibold text-[color:var(--muted)]">
                    {p.date}
                  </div>

                  <div className="mt-3 text-xl font-semibold text-[color:var(--navy)] group-hover:text-[color:var(--brand)] transition">
                    {p.title}
                  </div>

                  <p className="mt-3 text-sm text-[color:var(--muted)] leading-relaxed">
                    {p.excerpt}
                  </p>

                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--brand)]">
                    Read more
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--brand)]/10 transition group-hover:bg-[color:var(--brand)]/15">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M5 12H19M19 12L13 6M19 12L13 18"
                          stroke="var(--brand)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </NavLink>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="mt-12 rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-10 text-center">
            <div className="text-xl font-semibold text-[color:var(--navy)]">
              No posts found
            </div>
            <div className="mt-2 text-sm text-[color:var(--muted)]">
              Try a different search or category.
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogList;
